import Swal from "sweetalert2";
import { TemplateEngine } from "../shared/utils/TemplateEngine.js";
import { AppConfig } from "../shared/utils/AppConfig.js";
import { AuthService } from "../shared/services/AuthService.js";
import loginTemplate from "./templates/login.html?raw";
import registroTemplate from "./templates/registro.html?raw";
import recuperarTemplate from "./templates/recuperar.html?raw";
import restablecerTemplate from "./templates/restablecer.html?raw";

const CLAVE_MINIMA = 6;

// El parámetro del enlace del mail. Se llama "sec" y no "token" por dos motivos: en la barra de
// direcciones "token" anuncia que ahí va una credencial, y en el resto de la app "token" ya es el
// de sesión (el del header x-token). Mismo nombre en la URL y en el body que va al back.
const PARAM_SEC = "sec";

/**
 * El "sec" del enlace del mail: el hash de un solo uso que habilita a cambiar la clave. Se busca
 * primero en el fragmento (#restablecer?sec=...) y como respaldo en el query string
 * (?sec=...#restablecer): hay clientes de correo y filtros corporativos que reescriben el link y
 * pierden lo que va después del '#'.
 * El fragmento es el lugar preferido porque no viaja al servidor: no queda en los logs de acceso
 * ni en el header Referer.
 */
const leerSec = (params) => {
  const delHash = params.get(PARAM_SEC);
  if (delHash) return delHash.trim();
  const delQuery = new URLSearchParams(window.location.search).get(PARAM_SEC);
  return delQuery ? delQuery.trim() : "";
};

/** Saca el sec de la barra de direcciones para que no quede en el historial ni al compartir. */
const limpiarSecDeLaUrl = () => {
  // replaceState no dispara hashchange, asi que no re-entra al Router y no repinta la pantalla.
  window.history.replaceState(null, "", window.location.pathname + "#restablecer");
};

const app = () => document.getElementById("app");

const setCargando = (boton, cargando, textoOriginal) => {
  if (!boton) return;
  boton.disabled = cargando;
  boton.innerHTML = cargando
    ? '<span class="spinner-border spinner-border-sm me-2"></span>Procesando...'
    : textoOriginal;
};

const mostrarError = (id, mensaje) => {
  const el = document.getElementById(id);
  if (el) el.textContent = mensaje || "";
};

const alerta = (icono, titulo, texto) =>
  Swal.fire({
    icon: icono,
    title: titulo,
    text: texto,
    confirmButtonColor: "#12608f",
  });

export const AuthController = {
  renderLogin: () => {
    app().innerHTML = TemplateEngine.render(loginTemplate, {
      Version: AppConfig.app.version,
      // Solo mientras se demuestra con mocks: la ayuda desaparece sola al apagar VITE_USE_MOCK.
      EsDemo: AppConfig.api.useMock,
      UsuarioDemo: "30549561",
      ClaveDemo: "demo1234",
    });

    const form = document.getElementById("frmLogin");
    const boton = document.getElementById("btnIngresar");
    const textoBoton = boton.innerHTML;

    document.getElementById("btnVerClave")?.addEventListener("click", () => {
      const input = document.getElementById("clave");
      const icono = document.querySelector("#btnVerClave i");
      const oculta = input.type === "password";
      input.type = oculta ? "text" : "password";
      icono.className = oculta ? "fas fa-eye-slash" : "fas fa-eye";
    });

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      mostrarError("errUsuario", "");
      mostrarError("errClave", "");

      const usuario = document.getElementById("usuario").value.trim();
      const clave = document.getElementById("clave").value;

      if (!usuario) return mostrarError("errUsuario", "Ingresá tu DNI o correo electrónico.");
      if (!clave) return mostrarError("errClave", "Ingresá tu clave.");

      setCargando(boton, true, textoBoton);
      try {
        const resultado = await AuthService.login(usuario, clave);
        if (!resultado.ok) {
          setCargando(boton, false, textoBoton);
          return mostrarError("errClave", resultado.mensaje);
        }
        window.location.hash = "home";
      } catch (error) {
        console.error("Error en login:", error);
        setCargando(boton, false, textoBoton);
        alerta("error", "No pudimos conectarnos", "Revisá tu conexión e intentá de nuevo.");
      }
    });
  },

  renderRegistro: () => {
    app().innerHTML = TemplateEngine.render(registroTemplate, {
      Version: AppConfig.app.version,
    });

    const form = document.getElementById("frmRegistro");
    const boton = document.getElementById("btnRegistrar");
    const textoBoton = boton.innerHTML;

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      mostrarError("errRegistro", "");

      const valor = (id) => document.getElementById(id).value.trim();
      const clave = document.getElementById("clave").value;
      const clave2 = document.getElementById("clave2").value;

      if (!form.reportValidity()) return;
      if (clave.length < 6) return mostrarError("errRegistro", "La clave debe tener al menos 6 caracteres.");
      if (clave !== clave2) return mostrarError("errRegistro", "Las claves no coinciden.");

      const datos = {
        // La API lo declara int32: mandarlo como texto ("DNI") da 400 antes de entrar al handler.
        TipoDocumento: Number(valor("tipoDocumento")),
        Documento: valor("documento"),
        Email: valor("email"),
        Clave: clave,
      };

      setCargando(boton, true, textoBoton);
      try {
        const resultado = await AuthService.registro(datos);
        setCargando(boton, false, textoBoton);
        if (!resultado.ok) return mostrarError("errRegistro", resultado.mensaje);

        await alerta("success", "¡Listo!", resultado.mensaje);
        window.location.hash = "login";
      } catch (error) {
        console.error("Error en registro:", error);
        setCargando(boton, false, textoBoton);
        alerta("error", "No pudimos conectarnos", "Revisá tu conexión e intentá de nuevo.");
      }
    });
  },

  renderRecuperar: () => {
    app().innerHTML = TemplateEngine.render(recuperarTemplate, {
      Version: AppConfig.app.version,
    });

    const form = document.getElementById("frmRecuperar");
    const boton = document.getElementById("btnRecuperar");
    const textoBoton = boton.innerHTML;

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      mostrarError("errRecuperar", "");

      const usuario = document.getElementById("usuario").value.trim();
      if (!usuario) return mostrarError("errRecuperar", "Ingresá tu DNI o correo electrónico.");

      setCargando(boton, true, textoBoton);
      try {
        const resultado = await AuthService.recuperar(usuario);
        setCargando(boton, false, textoBoton);
        await alerta(resultado.ok ? "success" : "error", "Solicitud enviada", resultado.mensaje);
        if (resultado.ok) window.location.hash = "login";
      } catch (error) {
        console.error("Error en recuperar clave:", error);
        setCargando(boton, false, textoBoton);
        alerta("error", "No pudimos conectarnos", "Revisá tu conexión e intentá de nuevo.");
      }
    });
  },

  /** Destino del enlace que manda el back por mail: #restablecer?sec=<hash>. */
  renderRestablecer: (params) => {
    // El sec queda en el closure: la URL se limpia enseguida, pero un reintento del submit
    // (por ejemplo tras un corte de red) sigue teniendolo a mano.
    const sec = leerSec(params);

    app().innerHTML = TemplateEngine.render(restablecerTemplate, {
      Version: AppConfig.app.version,
      SecValido: !!sec,
    });

    if (!sec) return;
    limpiarSecDeLaUrl();

    const form = document.getElementById("frmRestablecer");
    const boton = document.getElementById("btnRestablecer");
    const textoBoton = boton.innerHTML;

    document.getElementById("btnVerClave")?.addEventListener("click", () => {
      const input = document.getElementById("clave");
      const icono = document.querySelector("#btnVerClave i");
      const oculta = input.type === "password";
      input.type = oculta ? "text" : "password";
      icono.className = oculta ? "fas fa-eye-slash" : "fas fa-eye";
    });

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      mostrarError("errRestablecer", "");

      // Sin .trim(): los espacios son parte de la clave, y por eso mismo se rechazan (abajo).
      const clave = document.getElementById("clave").value;
      const clave2 = document.getElementById("clave2").value;

      if (clave.length < CLAVE_MINIMA)
        return mostrarError("errRestablecer", `La clave debe tener al menos ${CLAVE_MINIMA} caracteres.`);
      if (/\s/.test(clave))
        return mostrarError("errRestablecer", "La clave no puede tener espacios.");
      if (clave !== clave2)
        return mostrarError("errRestablecer", "Las claves no coinciden.");

      setCargando(boton, true, textoBoton);
      try {
        const resultado = await AuthService.restablecer(sec, clave);
        setCargando(boton, false, textoBoton);
        if (!resultado.ok) return mostrarError("errRestablecer", resultado.mensaje);

        await alerta(
          "success",
          "Clave actualizada",
          "Tu nueva clave ya está activa. Te llevamos al inicio de sesión para que entres."
        );
        window.location.hash = "login";
      } catch (error) {
        console.error("Error al restablecer la clave:", error);
        setCargando(boton, false, textoBoton);
        alerta("error", "No pudimos conectarnos", "Revisá tu conexión e intentá de nuevo.");
      }
    });
  },
};
